import type { GameCard } from "../shared/deck";

export type PracticeLength = "original" | "20" | "10";
export const PRACTICE_LENGTHS: PracticeLength[] = ["original", "20", "10"];
const STORAGE_KEY = "pile-solo-length";

export interface PracticeGame {
  /** Captured at launch: changing a preference never alters an active round. */
  target: number;
  deck: GameCard[];
  own: GameCard | null;
  center: GameCard | null;
  score: number;
}

export function practiceTarget(length: PracticeLength, cardCount = 57): number {
  const wanted = length === "original" ? Math.floor(cardCount / 2) : Number(length);
  return Math.max(1, Math.min(cardCount - 1, wanted));
}

export function loadPracticeLength(): PracticeLength {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (PRACTICE_LENGTHS.includes(saved as PracticeLength)) return saved as PracticeLength;
  } catch {
    /* The default also works without storage. */
  }
  return "original";
}

export function savePracticeLength(length: PracticeLength): void {
  try {
    localStorage.setItem(STORAGE_KEY, length);
  } catch {
    /* The in-memory selection is still retained. */
  }
}

export function createPractice(cards: GameCard[], length: PracticeLength): PracticeGame {
  if (cards.length < 2) throw new RangeError("A practice round needs at least two cards.");
  const shuffled = [...cards];
  const center = shuffled.pop()!;
  const target = practiceTarget(length, cards.length);
  const deck = shuffled.slice(0, target);
  return { target, deck, own: deck.pop()!, center, score: 0 };
}

export function claimPractice(game: PracticeGame, symbolId: number): "wrong" | "hit" | "complete" | "finished" {
  if (!game.center || !game.own) return "finished";
  if (!game.own.symbols.includes(symbolId) || !game.center.symbols.includes(symbolId)) {
    return "wrong";
  }
  game.score++;
  game.center = game.own;
  game.own = game.deck.pop() ?? null;
  return game.own ? "hit" : "complete";
}
